/* eslint-disable prettier/prettier */
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import { Container, Button, Row, Col } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import { formatPrice } from '../../../utils/funcoes';

import SelectAsync from '../../SelectAsync';
import Input from '../../Input';
import Loading from '../../Loading';
import { ICartaoSaude } from '../../../pages/CartaoSaudeDetalhe';
import getResponseErrors from '../../../utils/getResponseErrors';

interface IReturnSimulacao {
  PlaValor: number;
  parcelas?: {
    CprValor: number;
    CprDataVenc: Date;
    CprHistorico: string;
  }[];
  geraNovoContrato: boolean;
  valorAdesao: number;
}

type ComponentProps = {
  PlaCodigo: number;
  updated_at: Date;
  handleFecharComponent(dados: ICartaoSaude): void;
  assinaturaComAdesao?: boolean;
};

interface ISubmitForm {
  PacCodigo: string;
  parentesco: string;
}

const CartaoSaudeAddDependente: React.FC<ComponentProps> = ({
  PlaCodigo,
  updated_at,
  handleFecharComponent,
  assinaturaComAdesao,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pacienteSelecionado, SetPacienteSelecionado] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [pacientesAdicionar, SetPacientesAdicionar] = useState<
    { PacCodigo: number; Nome: string; Parentesco: string }[]
  >([]);
  const [dadosSimulados, setDadosSimulados] = useState<
    | {
      valorParcela: string;
      valorPlano: string;
      numeroParcelas: number;
      geraNovoContrato: boolean;
      valorAdesao?: string;
    }
    | undefined
  >(undefined);
  const formRefBusca = useRef<FormHandles>(null);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const dependentes = pacientesAdicionar.map(pac => {
          return { DepPacCodigo: pac.PacCodigo, DepParentesco: pac.Parentesco };
        });
        const response = await api.post<IReturnSimulacao>(
          'planos/dependentesSimula',
          {
            PlaCodigo,
            dependentes,
            updated_at,
          },
        );

        const { parcelas, PlaValor, geraNovoContrato, valorAdesao } = response.data;

        const valorAdesaoFormatado = assinaturaComAdesao
          ? formatPrice(valorAdesao)
          : undefined;

        setDadosSimulados({
          numeroParcelas: parcelas?.length || 0,
          valorPlano: formatPrice(PlaValor),
          valorParcela: formatPrice(parcelas ? parcelas[0].CprValor : 0),
          geraNovoContrato,
          valorAdesao: valorAdesaoFormatado,
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível simular adição de dependentes',
          type: 'error',
          description: mensagemErro,
        });
        SetPacientesAdicionar(state => {
          state.pop();
          if (state) return state;
          return [];
        });
      } finally {
        setLoading(false);
      }
    }
    if (pacientesAdicionar && pacientesAdicionar.length > 0) loadDados();
  }, [pacientesAdicionar, PlaCodigo, updated_at, addToast, assinaturaComAdesao]);

  const handleRemovePaciente = useCallback(
    (PacCodigo: number) => {
      SetPacientesAdicionar(
        pacientesAdicionar.filter(pac => pac.PacCodigo !== PacCodigo),
      );
    },
    [pacientesAdicionar],
  );

  const handleSubmitForm = useCallback(
    async (dados: ISubmitForm) => {
      formRefBusca.current?.setErrors({});
      if (!dados.parentesco || dados.parentesco.length < 3) {
        formRefBusca.current?.setErrors({
          parentesco: 'Parentesco é obrigatório',
        });
      }
      if (!pacienteSelecionado) {
        formRefBusca.current?.setFieldError(
          'PacCodigo',
          'Selecione o paciente',
        );
        addToast({ type: 'error', title: 'Selecione o paciente' });
        return;
      }
      SetPacientesAdicionar(state => {
        const pac = state.find(
          paci => paci.PacCodigo === Number(dados.PacCodigo),
        );
        if (pac) {
          return state;
        }
        return [
          ...state,
          {
            PacCodigo: Number(dados.PacCodigo),
            Nome: pacienteSelecionado?.label || 'Erro',
            Parentesco: dados.parentesco.toUpperCase(),
          },
        ];
      });
      SetPacienteSelecionado(undefined);
      formRefBusca.current?.setFieldValue('PacCodigo', '');
      formRefBusca.current?.reset();
    },
    [pacienteSelecionado, addToast],
  );

  const handleSubmitDependentes = useCallback(async () => {
    setLoading(true);
    try {
      const dependentes = pacientesAdicionar.map(pac => {
        return { DepPacCodigo: pac.PacCodigo, DepParentesco: pac.Parentesco };
      });
      const response = await api.post<ICartaoSaude>('planos/dependentes', {
        PlaCodigo,
        dependentes,
        updated_at,
      });

      handleFecharComponent(response.data);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível salvar os dependentes',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [
    PlaCodigo,
    updated_at,
    pacientesAdicionar,
    handleFecharComponent,
    addToast,
  ]);

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}

      <Form ref={formRefBusca} onSubmit={handleSubmitForm}>
        <Row noGutters>
          <Col sm={8} className="pr-1">
            <SelectAsync
              name="PacCodigo"
              label="Paciente"
              isClearable
              urlApi="pacientes/pacientesemcartao?termoBusca="
              placeholder="Digite para buscar"
              handleSelectChange={dados => SetPacienteSelecionado(dados)}
            />
          </Col>
          <Col sm={2} className="pr-1">
            <Input name="parentesco" label="Parentesco" required />
          </Col>
          <Col sm={2}>
            <Button
              type="submit"
              size="sm"
              variant="primary"
              style={{ marginTop: '19px' }}
            >
              Selecionar
            </Button>
          </Col>
        </Row>
      </Form>

      {pacientesAdicionar && pacientesAdicionar.length > 0 && (
        <div style={{ fontSize: '.8rem' }}>
          <Row className="mt-3">
            <Col sm={8}>
              <b>Paciente</b>
            </Col>
            <Col sm={2}>
              <b>Parentesco</b>
            </Col>

            <Col />
          </Row>
          {pacientesAdicionar.map(pac => (
            <Row key={`pac-add-${pac.PacCodigo}`} className="border-bottom">
              <Col sm={8} className="py-1">
                {pac.Nome}
              </Col>
              <Col sm={2} className="py-1">
                {pac.Parentesco}
              </Col>
              <Col sm={2}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleRemovePaciente(pac.PacCodigo)}
                >
                  <FaTrash size={14} />
                </Button>
              </Col>
            </Row>
          ))}
          <div className="d-flex justify-content-between align-items-baseline mt-2 py-2">
            {dadosSimulados && !assinaturaComAdesao && (
              <p style={{ fontSize: '.9rem' }}>
                O valor do plano sera: <b>{dadosSimulados.valorPlano}</b>.
                {dadosSimulados.numeroParcelas > 0 && (
                  <span>
                    Parcela adicional <b>{dadosSimulados.numeroParcelas}</b> de{' '}
                    <b>{dadosSimulados.valorParcela}</b>
                  </span>
                )}
                {dadosSimulados.geraNovoContrato && (
                  <div className="alert alert-warning px-3 py-1 mt-2">
                    <h5>Atenção!</h5>
                    Essa operação írá gerar um novo contrato, começando da
                    parcela 1 até a 12.
                  </div>
                )}
              </p>
            )}
            {dadosSimulados && assinaturaComAdesao && (
              <p style={{ fontSize: '.9rem' }}>
                O valor do plano sera: <b>{dadosSimulados.valorPlano}</b>.
                <div className="alert alert-warning px-3 py-1 mt-2">
                  <h5>Atenção!</h5>
                  <p>{`O valor de adesão será de ${dadosSimulados.valorAdesao || ''
                    }.`}
                  </p>
                  <p>Este valor pode ser dividido em até 6x no cartão de crédito.
                  </p>
                </div>
              </p>
            )}

            <Button
              type="button"
              size="sm"
              variant="success"
              onClick={handleSubmitDependentes}
            >
              INCLUIR DEPENDENTE
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default CartaoSaudeAddDependente;
